import urlUtil from 'url';
import { createReducer } from 'redux-act';
import { createSelector } from 'reselect';
import { createResource } from '@gandi/resource/redux';
import { invalidateMeans } from '../means';
import { selectRaw as selectRawState } from '../utils';
export const baseUrl = '/money/mycards';
export const resource = createResource('billing.money.mycards');
const computeLabel = ({
  sharingId
}) => sharingId;
export const selectIsFetching = (state, props) => resource.selectIsFetching(state, computeLabel(props));
export const selectIsLoaded = (state, props) => resource.selectIsLoaded(state, computeLabel(props));
export const selectIsFailure = (state, props) => resource.selectIsFailure(state, computeLabel(props));
export const selectIsToRefetch = (state, props) => resource.selectIsToRefetch(state, computeLabel(props));
export const selectIsCreatingOne = (state, props) => resource.selectIsCreatingOne(state, computeLabel(props));
export const selectIsDeletingOne = (state, props) => resource.selectIsDeletingOne(state, String(props));
export const selectIsPatchingOne = (state, props) => resource.selectIsPatchingOne(state, String(props));
export const selectIsFetchingOne = (state, props) => resource.selectIsFetchingOne(state, String(props));
export const selectIsLoadedOne = (state, props) => resource.selectIsLoadedOne(state, String(props));
export const selectIsFailureOne = (state, props) => resource.selectIsFailureOne(state, String(props));
export const selectIsToRefetchOne = (state, props) => resource.selectIsToRefetchOne(state, String(props));
const selectRaw = createSelector([selectRawState], raw => raw.myCards);
const selectRawMyCards = createSelector([selectRaw, (_, props) => computeLabel(props)], (raw, label) => raw && raw[label]);
export const selectMyCards = createSelector([selectRaw, (_, props) => computeLabel(props)], (raw, label) => raw && raw[label] && Object.values(raw[label]));
export const selectMyCard = createSelector([selectRawMyCards, (_, props) => props.myCardId], (raw, id) => raw && raw[String(id)]);
export const selectMyActiveCard = createSelector([selectMyCards], cards => cards && cards.find(card => card.active));
export const selectMyCardByName = createSelector([selectMyCards, (_, {
  name
}) => name], (cards, name) => cards && cards.find(card => card.name === name));
export const fetch = options => dispatch => dispatch(resource.fetch(baseUrl, {
  on2XX: response => response,
  not2XX: response => Promise.reject(response),
  ...options,
  params: {
    ...options.params,
    sharing_id: options.sharingId
  },
  label: computeLabel({
    sharingId: options.sharingId
  })
}));
export const createOne = (payload, options) => dispatch => dispatch(resource.createOne(baseUrl, payload, {
  on2XX: response => response,
  not2XX: response => Promise.reject(response),
  ...options,
  params: {
    ...options.params,
    sharing_id: options.sharingId
  }
}));
export const fetchOne = (myCardId, options) => dispatch => dispatch(resource.fetchOne(`${baseUrl}/${myCardId}`, {
  on2XX: response => response,
  not2XX: response => Promise.reject(response),
  ...options,
  params: {
    ...options.params,
    sharing_id: options.sharingId
  },
  label: String(myCardId)
}));
export const patchOne = (myCardId, payload, options) => dispatch => dispatch(resource.patchOne(`${baseUrl}/${myCardId}`, payload, {
  on2XX: response => response,
  not2XX: response => Promise.reject(response),
  ...options,
  params: {
    ...options.params,
    sharing_id: options.sharingId
  },
  label: String(myCardId)
}));
export const deleteOne = (myCardId, options) => dispatch => dispatch(resource.deleteOne(`${baseUrl}/${myCardId}`, {
  on2XX: response => response,
  not2XX: response => Promise.reject(response),
  ...options,
  params: {
    ...options.params,
    sharing_id: options.sharingId
  },
  label: String(myCardId)
}));
export const toggleActivation = (myCardId, options) => (dispatch, getState) => {
  const state = getState();
  const {
    sharingId
  } = options;
  const myCard = selectMyCard(state, {
    myCardId,
    sharingId
  });
  if (!myCard) {
    const error = new Error(`Could not find MyCard ${myCardId}, refusing to toggle activation`);
    throw error;
  }
  return dispatch(patchOne(myCardId, {
    active: !myCard.active
  }, {
    on2XX: () => {
      dispatch(invalidateMeans({
        sharingId
      }));
      dispatch(fetch({
        sharingId
      }));
    },
    ...options
  }));
};
export const formatReturnUrl = (returnUrl, orderId) => {
  const parsed = urlUtil.parse(returnUrl, true);
  const formattedRedirectTo = urlUtil.format({
    ...parsed,
    query: {
      ...parsed.query,
      ...(orderId ? {
        order: orderId
      } : {}),
      retry: String(window.location)
    }
  });
  return window.btoa(formattedRedirectTo);
};
const initialState = {};
const handleFetch = (state, {
  response: {
    data
  },
  label
}) => ({
  ...state,
  [label]: {
    ...(state[label] || {}),
    ...data.reduce((acc, current) => ({
      ...acc,
      [current.id]: {
        ...(state[current.id] || {}),
        ...current
      }
    }), {})
  }
});
const handleFetchOne = (state, {
  label: id,
  sharingId,
  response: {
    data
  }
}) => ({
  ...state,
  [sharingId]: {
    ...(state[sharingId] || {}),
    [id]: data
  }
});
const handleDeleteOne = (state, {
  label: id,
  sharingId
}) => {
  const {
    [sharingId]: currentState,
    ...newState
  } = state;
  const {
    [id]: _,
    ...exludeDeletedCurrentState
  } = currentState;
  return {
    ...newState,
    [sharingId]: {
      ...exludeDeletedCurrentState
    }
  };
};
const reducer = createReducer({
  [resource.fetch.success.getType()]: handleFetch,
  [resource.fetchOne.success.getType()]: handleFetchOne,
  [resource.deleteOne.success.getType()]: handleDeleteOne
}, initialState);
export default reducer;